import React, { useState, useEffect, useRef } from 'react'
import { DASHBOARDS, DEFAULT_DASHBOARD_ID, MAX_SELECTIONS } from '../../config/config'
import DashboardEmbed from './DashboardEmbed'

// (moved to config)

const SupportDashboardsTool: React.FC = () => {
  const [selectedIds, setSelectedIds] = useState<string[]>(() => {
    // Default to the first configured dashboard (or fallback constant)
    return DASHBOARDS && DASHBOARDS.length > 0 ? [DASHBOARDS[0].id] : [DEFAULT_DASHBOARD_ID]
  })
  const [layoutMode, setLayoutMode] = useState<'grid' | 'side' | 'stack'>('grid')
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const panelRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const [fullscreenId, setFullscreenId] = useState<string | null>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleCheckboxChange = (id: string, checked: boolean) => {
    setSelectedIds(prev => {
      if (checked) {
        if (prev.length >= MAX_SELECTIONS) return prev
        return [...prev, id]
      } else {
        return prev.filter(sid => sid !== id)
      }
    })
  }

  const handleRemove = (id: string) => {
    setSelectedIds(prev => prev.filter(sid => sid !== id))
  }

  const toggleFullscreen = async (id: string) => {
    const el = panelRefs.current[id]
    if (!el) return

    try {
      if (document.fullscreenElement === el) {
        await document.exitFullscreen()
      } else {
        await el.requestFullscreen()
      }
    } catch (err) {
      // silent fail; optionally show an alert via AlertContext
      // console.error('Fullscreen toggle failed', err)
    }
  }

  useEffect(() => {
    const onFsChange = () => {
      const fe = document.fullscreenElement
      if (!fe) {
        setFullscreenId(null)
        return
      }
      const found = Object.entries(panelRefs.current).find(([, node]) => node === fe)
      setFullscreenId(found ? found[0] : null)
    }

    document.addEventListener('fullscreenchange', onFsChange)
    return () => document.removeEventListener('fullscreenchange', onFsChange)
  }, [])

  // Lock body scroll while any panel is fullscreen
  useEffect(() => {
    const prev = document.body.style.overflow
    if (fullscreenId) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = prev || ''
    }
  }, [fullscreenId])

  // Support Escape key to exit fullscreen (browsers typically do this, but keep as a fallback)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && document.fullscreenElement) {
        document.exitFullscreen().catch(() => {})
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const selectedDashboards = DASHBOARDS.filter(d => selectedIds.includes(d.id))
  const reachedLimit = selectedIds.length >= MAX_SELECTIONS

  const helperText =
    selectedDashboards.length === 0
      ? `Choose up to ${MAX_SELECTIONS} dashboards.`
      : `Now showing: ${selectedDashboards.map(d => d.title).join(' and ')}.`

  return (
    <div className="w-full  mx-auto px-2 py-2 sm:px-4 sm:py-4 lg:px-6 lg:py-6 bg-neutral-50 min-h-screen">
      {/* Header */}
      <div className="bg-riddell-red text-white rounded-t-lg shadow-lg mb-3 sm:mb-4 lg:mb-6 w-full">
        <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 lg:py-6">
          <div className="flex flex-col sm:flex-row items-center sm:space-x-4 space-y-2 sm:space-y-0 w-full">
            <div className="bg-white p-1.5 sm:p-2 rounded flex-shrink-0">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-riddell-red rounded flex items-center justify-center">
                <span className="text-white font-bold text-sm sm:text-lg">R</span>
              </div>
            </div>
            <div className="text-center sm:text-left flex-1 min-w-0">
              <h1
                className="text-lg sm:text-2xl lg:text-3xl font-heading font-black tracking-tight"
                id="page-title"
              >
                Support Dashboards
              </h1>
              <p
                className="text-sm sm:text-base lg:text-lg font-medium opacity-90"
                id="page-description"
              >
                View embedded support reports and switch dashboards by organization need.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main card */}
      <div className="bg-white rounded-lg shadow-xl border border-neutral-200 w-full">
        <div className="p-3 sm:p-4 lg:p-6 w-full">

          {/* Step 1: Dashboard Selection */}
          <div
            className="bg-neutral-50 border-l-4 border-riddell-red p-3 sm:p-4 lg:p-6 rounded-lg w-full"
            role="group"
            aria-labelledby="dashboard-selection-heading"
          >
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3 sm:mb-4">
              <div className="bg-riddell-red text-white rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-xs sm:text-sm lg:text-base flex-shrink-0">
                1
              </div>
              <h2
                id="dashboard-selection-heading"
                className="text-base sm:text-lg lg:text-xl font-heading font-bold text-riddell-black"
              >
                SELECT SUPPORT DASHBOARD
              </h2>
            </div>
            <div className="flex flex-col md:flex-row md:items-end gap-6">
  {/* Dashboard Selection */}
  <div className="w-full md:max-w-xl">
    <label
      htmlFor="dashboard-dropdown-btn"
      className="block text-sm font-bold text-neutral-700 uppercase tracking-wide mb-1"
    >
      Dashboard
    </label>

    {/* Custom multi-select dropdown */}
    <div className="relative" ref={dropdownRef}>
      <button
        id="dashboard-dropdown-btn"
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        aria-expanded={isOpen}
        aria-controls="dashboard-dropdown-panel"
        aria-haspopup="listbox"
        className="w-full flex items-center justify-between px-4 py-3 bg-white border border-neutral-300 rounded-lg text-left text-sm sm:text-base font-medium text-neutral-800 shadow-sm hover:border-riddell-red focus:outline-none focus:ring-2 focus:ring-riddell-red focus:ring-offset-1 transition-colors"
      >
        <span>
          {selectedDashboards.length === 0
            ? 'Select dashboards'
            : selectedDashboards.length === 1
            ? selectedDashboards[0].title
            : `${selectedDashboards.length} dashboards selected`}
        </span>

        <span className="flex items-center gap-2 flex-shrink-0 ml-2">
          <span className="text-xs text-neutral-500 font-normal">
            {selectedIds.length} selected
          </span>

          <svg
            className={`w-4 h-4 text-neutral-500 transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </span>
      </button>

      {isOpen && (
        <div
          id="dashboard-dropdown-panel"
          role="listbox"
          aria-multiselectable="true"
          aria-label="Select dashboards"
          className="absolute z-20 top-full mt-2 left-0 right-0 bg-white border border-neutral-200 rounded-lg shadow-xl max-h-60 overflow-y-auto p-2 grid grid-cols-2 gap-2"
        >
          {DASHBOARDS.map(dashboard => {
            const isChecked = selectedIds.includes(dashboard.id)
            const isDisabled = reachedLimit && !isChecked

            return (
              <label
                key={dashboard.id}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  isDisabled
                    ? 'opacity-40 cursor-not-allowed'
                    : 'cursor-pointer hover:bg-neutral-100'
                }`}
                role="option"
                aria-selected={isChecked}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  disabled={isDisabled}
                  onChange={e =>
                    handleCheckboxChange(dashboard.id, e.target.checked)
                  }
                  className="w-4 h-4 accent-riddell-red flex-shrink-0"
                  aria-label={dashboard.title}
                />
                <span className="text-sm font-medium text-neutral-800">
                  {dashboard.title}
                </span>
              </label>
            )
          })}
        </div>
      )}
    </div>

    <p className="mt-1.5 text-xs text-neutral-500" aria-live="polite">
      {helperText}
    </p>
  </div>

  {/* Layout Selection */}
 <div className="w-full md:w-56 self-start">
    <label
      htmlFor="layout-select"
       className="block text-sm font-bold text-neutral-700 uppercase tracking-wide mb-1"
    >
      Layout
    </label>

    <div
      id="layout-select"
      className="flex bg-white border border-neutral-300 rounded-lg shadow-sm overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setLayoutMode('grid')}
        aria-pressed={layoutMode === 'grid'}
        className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
          layoutMode === 'grid'
            ? 'bg-riddell-red text-white'
            : 'bg-white text-neutral-800 hover:bg-neutral-100'
        }`}
      >
        Grid
      </button>

      <button
        type="button"
        onClick={() => setLayoutMode('stack')}
        aria-pressed={layoutMode === 'stack'}
        className={`flex-1 px-4 py-3 text-sm font-medium border-l border-neutral-300 transition-colors ${
          layoutMode === 'stack'
            ? 'bg-riddell-red text-white'
            : 'bg-white text-neutral-800 hover:bg-neutral-100'
        }`}
      >
        Stack
      </button>
    </div>

  </div>
</div>
           
          </div>

          {/* Dashboard panels */}
          {selectedDashboards.length > 0 && (
            <div
              className={`mt-4 sm:mt-6 ${
                layoutMode === 'grid'
                  ? `grid gap-4 ${selectedDashboards.length === 1 ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`
                  : layoutMode === 'side'
                  ? 'flex gap-4 overflow-x-auto py-1'
                  : 'flex flex-col gap-4'
              }`}
              aria-label="Embedded dashboard view"
            >
              {selectedDashboards.map(dashboard => (
                <div
                  key={dashboard.id}
                  ref={el => (panelRefs.current[dashboard.id] = el)}
                  className={
                    `border border-neutral-200 rounded-xl overflow-hidden bg-white shadow-md ${
                      layoutMode === 'side' ? 'flex-shrink-0 w-[min(700px,48vw)]' : 'w-full'
                    }`
                  }
                >
                  {/* Panel header */}
                  <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-neutral-200 bg-neutral-50">
                    <h3 className="text-sm font-semibold text-neutral-800 truncate">{dashboard.title}</h3>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => toggleFullscreen(dashboard.id)}
                        className="flex-shrink-0 px-2 py-1 text-xs font-semibold text-neutral-700 bg-white border border-neutral-300 rounded-full hover:bg-neutral-100 hover:border-neutral-400 transition-colors focus:outline-none focus:ring-2 focus:ring-riddell-red focus:ring-offset-1"
                        aria-pressed={fullscreenId === dashboard.id}
                        aria-label={fullscreenId === dashboard.id ? `Exit fullscreen ${dashboard.title}` : `Open ${dashboard.title} fullscreen`}
                      >
                        {fullscreenId === dashboard.id ? 'Exit' : 'Full'}
                      </button>

                      <button
                        type="button"
                        onClick={() => handleRemove(dashboard.id)}
                        className="flex-shrink-0 px-3 py-1 text-xs font-semibold text-neutral-700 bg-white border border-neutral-300 rounded-full hover:bg-neutral-100 hover:border-neutral-400 transition-colors focus:outline-none focus:ring-2 focus:ring-riddell-red focus:ring-offset-1"
                        aria-label={`Remove ${dashboard.title}`}
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  <DashboardEmbed
                    dashboard={dashboard}
                    height={fullscreenId === dashboard.id ? '100vh' : 'min(68vh, 900px)'}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SupportDashboardsTool
